import { validateId } from './utils/id';
import dayjs, { type Dayjs } from './utils/dayjs';
import { validate } from 'email-validator';

export const MAX_LENGTH_DEFAULT_STRING = 255;

export enum ValidationResult {
  TOO_LONG = 'TOO_LONG',
  WRONG_FORMAT = 'WRONG_FORMAT',
  EMPTY = 'EMPTY',
  OUT_OF_RANGE = 'OUT_OF_RANGE',
  OUT_OF_RANGE_MIN = 'OUT_OF_RANGE_MIN',
  OUT_OF_RANGE_MAX = 'OUT_OF_RANGE_MAX',
  VALID = 'VALID',
  NOT_EQUAL = 'NOT_EQUAL',
  DUPLICATE = 'DUPLICATE',
}

export enum ValidationMode {
  ON_BLUR = 'ON_BLUR',
  ALWAYS = 'ALWAYS',
  OFF = 'OFF',
}

export type ValidatorFn<T = string> = (input: T) => ValidationResult;

type OptionalString = string | null | undefined;

export function normalizeString(string: string): string;
export function normalizeString(string: OptionalString): string | undefined;
export function normalizeString(string: OptionalString): string | undefined {
  return string?.trim();
}

export const validateEmail = (email: OptionalString) => {
  if (email === undefined || email === null) {
    return ValidationResult.EMPTY;
  }
  if (typeof email !== 'string') {
    return ValidationResult.WRONG_FORMAT;
  }
  email = normalizeString(email);
  if (!email) {
    return ValidationResult.EMPTY;
  }
  if (email.length > MAX_LENGTH_DEFAULT_STRING) {
    return ValidationResult.TOO_LONG;
  }
  if (!validate(email)) {
    return ValidationResult.WRONG_FORMAT;
  }
  return ValidationResult.VALID;
};

export function validateDefined(value: unknown) {
  if (value === undefined || value === null) {
    return ValidationResult.EMPTY;
  }
  return ValidationResult.VALID;
}

export function validateUUID(value: string) {
  if (validateId(value)) {
    return ValidationResult.VALID;
  }
  return ValidationResult.WRONG_FORMAT;
}

export function validateNonEmptyArray(value: unknown) {
  if (!Array.isArray(value) || value.length === 0) {
    return ValidationResult.EMPTY;
  }
  return ValidationResult.VALID;
}

export const validateString = (
  string: OptionalString,
  maxLength = MAX_LENGTH_DEFAULT_STRING,
  regex?: string,
): ValidationResult => {
  if (string === undefined || string === null) {
    return ValidationResult.EMPTY;
  }
  if (typeof string !== 'string') {
    return ValidationResult.WRONG_FORMAT;
  }
  string = normalizeString(string);
  if (!string) {
    return ValidationResult.EMPTY;
  }
  if (string.length > maxLength) {
    return ValidationResult.TOO_LONG;
  }
  if (regex && !new RegExp(regex).exec(string)) {
    return ValidationResult.WRONG_FORMAT;
  }
  return ValidationResult.VALID;
};

export const validateRegex = (
  regexString: OptionalString,
): ValidatorFn<OptionalString> => {
  if (!regexString) {
    return () => ValidationResult.VALID;
  }

  const regex = new RegExp(regexString);
  return (input: OptionalString) => {
    if (input === undefined || input === null) {
      return ValidationResult.EMPTY;
    }
    if (typeof input !== 'string') {
      return ValidationResult.WRONG_FORMAT;
    }
    return regex.exec(input.trim())
      ? ValidationResult.VALID
      : ValidationResult.WRONG_FORMAT;
  };
};

export const validateStringsAreEqual = (
  strings?: OptionalString[],
): ValidationResult => {
  const equal = strings
    ?.map((string) => normalizeString(string))
    .every((value, _index, array) => value === array[0]);
  return equal ? ValidationResult.VALID : ValidationResult.NOT_EQUAL;
};

const PHONE_REGEX =
  '^\\+?\\d{1,4}?[-.\\s]?\\(?\\d{1,3}?\\)?[-.\\s]?\\d{1,4}[-.\\s]?\\d{1,4}[-.\\s]?\\d{1,9}$';

export const MAX_LENGTH_PHONE_NUMBER = 25;

export const validatePhoneNumber = (value: OptionalString) =>
  validateString(value, MAX_LENGTH_PHONE_NUMBER, PHONE_REGEX);

export const validateUrl = (
  value: OptionalString,
  maxLength = MAX_LENGTH_DEFAULT_STRING,
) => {
  if (value === undefined || value === null) {
    return ValidationResult.EMPTY;
  }
  if (typeof value !== 'string') {
    return ValidationResult.WRONG_FORMAT;
  }

  value = normalizeString(value);

  if (!value) {
    return ValidationResult.EMPTY;
  }

  if (value.length > maxLength) {
    return ValidationResult.TOO_LONG;
  }

  let parsedUrl: string;
  try {
    parsedUrl = new URL(value).toString();
  } catch {
    try {
      // Try being more lax in the validation by prepending a default protocol
      const laxValue = 'https://' + value;
      parsedUrl = new URL(laxValue).toString();
    } catch {
      return ValidationResult.WRONG_FORMAT;
    }
  }

  if (new RegExp(`^(https://)?${value}/?$`).exec(parsedUrl)) {
    return ValidationResult.VALID;
  } else {
    return ValidationResult.WRONG_FORMAT;
  }
};

export const validatePossibleValues = (
  value: OptionalString,
  possibleValues: Record<string, unknown>,
): ValidationResult => {
  if (value === undefined || value === null) {
    return ValidationResult.EMPTY;
  }
  return Object.prototype.hasOwnProperty.call(possibleValues, value)
    ? ValidationResult.VALID
    : ValidationResult.WRONG_FORMAT;
};

export const validateEnum = (
  value: string | number | undefined | null,
  enumObject: Record<string | number, string | number>,
): ValidationResult => {
  if (value === undefined || value === null) {
    return ValidationResult.EMPTY;
  }
  return Object.values(enumObject).includes(value)
    ? ValidationResult.VALID
    : ValidationResult.WRONG_FORMAT;
};

export const validateDateRange = (
  startDate?: Dayjs | string,
  endDate?: Dayjs | string,
) => {
  return (input: Dayjs | null | undefined) => {
    if (!input) {
      return ValidationResult.EMPTY;
    }
    if (typeof startDate === 'string') {
      startDate = dayjs(startDate);
    }
    if (typeof endDate === 'string') {
      endDate = dayjs(endDate);
    }
    if (startDate?.isAfter(input)) {
      return ValidationResult.WRONG_FORMAT;
    }
    if (endDate?.isBefore(input)) {
      return ValidationResult.WRONG_FORMAT;
    }
    return ValidationResult.VALID;
  };
};
